import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../auth/AuthProvider';
import { Calendar, Mail, MapPin, Phone, User, School, ChevronLeft, UserCheck, X, Save, Edit, Trash2, Plus, BookOpen, GraduationCap, AlertCircle, CheckCircle, Loader} from 'lucide-react';
import { format } from 'date-fns';
import { useTranslation } from 'react-i18next';

const TeacherProfile = () => {
  const { teacherId } = useParams();
  const navigate = useNavigate();
  const { api, user } = useAuth();
  const { t } = useTranslation('teacherprofile');
  
  // State for teacher data
  const [teacher, setTeacher] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // State for assigned subjects
  const [assignedSubjects, setAssignedSubjects] = useState([]);
  const [loadingAssignments, setLoadingAssignments] = useState(false);
  
  // State for assignment modal
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [editingAssignment, setEditingAssignment] = useState(null);
  const [deletingAssignment, setDeletingAssignment] = useState(null);
  
  // State for form data
  const [classes, setClasses] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [academicYears, setAcademicYears] = useState([]);
  const [assignmentForm, setAssignmentForm] = useState({
    subject_id: '',
    class_id: '',
    stream_id: '',
    academic_year_id: ''
  });
  
  // State for loading and messages
  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [loadingData, setLoadingData] = useState(false);

  // Fetch teacher data
  useEffect(() => {
    const fetchTeacherData = async () => {
      try {
        const response = await api.get(`/api/v1/teachers/schools/${user.school_reg}/teachers/${teacherId}`);
        setTeacher(response.data);
      } catch (error) {
        handleApiError(error);
      } finally {
        setLoading(false);
      }
    };

    fetchTeacherData();
  }, [teacherId, api, user.school_reg]);

  // Fetch assigned subjects
  useEffect(() => {
    if (teacher) {
      fetchAssignedSubjects();
    }
  }, [teacher]);

  const fetchAssignedSubjects = async () => {
    setLoadingAssignments(true);
    try {
      const response = await api.get(`/api/v1/assign-subjects/teacher/${teacherId}/school/${user.school_id}/subjects`);
      setAssignedSubjects(response.data);
    } catch (error) {
      handleApiError(error);
    } finally {
      setLoadingAssignments(false);
    }
  };

  // Fetch dropdown data
  const fetchDropdownData = async () => {
    setLoadingData(true);
    try {
      const [classesRes, subjectsRes, academicYearsRes] = await Promise.all([
        api.get(`/api/v1/admin/schools/${user.school_reg}/classes`),
        api.get('/api/v1/subjects'),
        api.get('/api/v1/academic-years')
      ]);
      
      setClasses(classesRes.data);
      setSubjects(subjectsRes.data);
      setAcademicYears(academicYearsRes.data);
    } catch (error) {
      handleApiError(error);
    } finally {
      setLoadingData(false);
    }
  };

  // Handle API errors
  const handleApiError = (error) => {
    if (error.response?.data?.detail) {
      if (Array.isArray(error.response.data.detail)) {
        // Handle validation errors
        const errorMessages = error.response.data.detail.map(err => err.msg).join(', ');
        setMessage({ type: 'error', text: errorMessages });
      } else {
        // Handle general errors
        setMessage({ type: 'error', text: error.response.data.detail });
      }
    } else {
      setMessage({ type: 'error', text: error.message || t('errors.general') });
    }
  };

  // Clear message after 5 seconds
  useEffect(() => {
    if (message.text) {
      const timer = setTimeout(() => {
        setMessage({ type: '', text: '' });
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [message]);

  const getRandomColor = (name) => {
    const colors = [
      'bg-blue-600', 'bg-purple-600', 'bg-pink-600', 'bg-red-600', 
      'bg-emerald-600', 'bg-yellow-600', 'bg-cyan-600', 'bg-indigo-600'
    ];
    const index = name.split('')
      .reduce((acc, char) => acc + char.charCodeAt(0), 0) % colors.length;
    return colors[index];
  };

  const openAssignModal = () => {
    setAssignmentForm({
      subject_id: '',
      class_id: '',
      stream_id: '',
      academic_year_id: ''
    });
    setShowAssignModal(true);
    fetchDropdownData();
  };

  const openEditModal = (assignment) => {
    setEditingAssignment(assignment);
    setAssignmentForm({
      subject_id: assignment.subject_id,
      class_id: assignment.class_id,
      stream_id: assignment.stream_id,
      academic_year_id: assignment.academic_year_id
    });
    setShowEditModal(true);
    fetchDropdownData();
  };

  const openDeleteModal = (assignment) => {
    setDeletingAssignment(assignment);
    setShowDeleteModal(true);
  };

  const handleAssignSubject = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    
    try {
      const payload = [{
        subject_id: parseInt(assignmentForm.subject_id),
        class_id: parseInt(assignmentForm.class_id),
        stream_id: parseInt(assignmentForm.stream_id),
        academic_year_id: parseInt(assignmentForm.academic_year_id)
      }];
      
      await api.post(`/api/v1/assign-subjects/teacher/${teacherId}/school/${user.school_id}/assign-subject`, payload);
      setMessage({ type: 'success', text: t('messages.assignmentSuccess') });
      setShowAssignModal(false);
      fetchAssignedSubjects();
    } catch (error) {
      handleApiError(error);
    } finally {
      setSubmitting(false);
    }
  };

  const handleUpdateAssignment = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    
    try {
      await api.put(`/api/v1/assign-subjects/${editingAssignment.id}/teacher/school/${user.school_id}/update-subject`, assignmentForm);
      setMessage({ type: 'success', text: t('messages.updateSuccess') });
      setShowEditModal(false);
      fetchAssignedSubjects();
    } catch (error) {
      handleApiError(error);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteAssignment = async () => {
    setSubmitting(true);
    
    try {
      await api.delete(`/api/v1/assign-subjects/${deletingAssignment.id}/teacher/school/${user.school_id}/delete-subject`);
      setMessage({ type: 'success', text: t('messages.deleteSuccess') });
      setShowDeleteModal(false);
      fetchAssignedSubjects();
    } catch (error) {
      handleApiError(error);
    } finally {
      setSubmitting(false);
    }
  };

  const getSubjectName = (subjectId) => {
    const subject = subjects.find(s => s.id === subjectId);
    return subject ? subject.subject_name : 'N/A';
  };

  const getClassName = (classId) => {
    const classItem = classes.find(c => c.id === classId);
    return classItem ? classItem.name : 'N/A';
  };

  const getStreamName = (streamId) => {
    for (const classItem of classes) {
      const stream = classItem.streams?.find(s => s.id === streamId);
      if (stream) return stream.name;
    }
    return 'N/A';
  };

  const getAcademicYearName = (yearId) => {
    const year = academicYears.find(y => y.id === yearId);
    return year ? year.year_name : 'N/A';
  };

  const getStreamsForClass = (classId) => {
    const classItem = classes.find(c => c.id === parseInt(classId));
    return classItem ? classItem.streams || [] : [];
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#0E5252]"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-red-500 text-lg">{t('errors.general')}: {error}</div>
      </div>
    );
  }

  if (!teacher) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-gray-500 text-lg">{t('errors.notFound')}</div>
      </div>
    );
  }

  const rtlClass = document.documentElement.dir === 'rtl' ? 'rtl text-right' : '';

  return (
    <div className={`container mx-auto max-w-7xl p-4 ${rtlClass}`}>
      {/* Message Display */}
      {message.text && (
        <div className={`mb-6 p-4 rounded-lg border-l-4 ${
          message.type === 'success' 
            ? 'bg-green-50 border-green-400 text-green-700' 
            : 'bg-red-50 border-red-400 text-red-700'
        }`}>
          <div className="flex items-center">
            {message.type === 'success' ? (
              <CheckCircle className="w-5 h-5 mr-2" />
            ) : (
              <AlertCircle className="w-5 h-5 mr-2" />
            )}
            <span>{message.text}</span>
          </div>
        </div>
      )}

      {/* Header Section */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8 gap-4">
        <button 
          onClick={() => navigate(-1)}
          className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors"
        >
          <ChevronLeft className="w-4 h-4" />
          <span>{t('navigation.backToTeachers')}</span>
        </button>
        
        <div className="flex flex-wrap gap-3">
          <button
            onClick={openAssignModal}
            className="px-4 py-2 bg-[#0E5252] text-white rounded-lg hover:bg-[#0E5252]/90 transition-colors flex items-center gap-2"
          >
            <UserCheck className="w-4 h-4" />
            {t('buttons.assignSubjects')}
          </button>
          <button
            onClick={() => navigate(`/edit-teacher/${teacherId}`)}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2"
          >
            <Edit className="w-4 h-4" />
            {t('buttons.editProfile')}
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
        {/* Teacher Basic Info Card */}
        <div className="bg-white rounded-xl shadow-lg overflow-hidden">
          <div className="p-6 border-b border-gray-100">
            <h2 className="text-2xl font-semibold text-gray-800">{t('sections.teacherProfile')}</h2>
          </div>
          <div className="p-6">
            <div className="flex flex-col items-center mb-6">
              <div className={`w-32 h-32 rounded-full flex items-center justify-center text-3xl text-white ${getRandomColor(teacher.name)}`}>
                {teacher.name.charAt(0)}
              </div>
              <h2 className="text-2xl font-bold mt-4 text-gray-800">{teacher.name}</h2>
              <p className="text-gray-600">{t('labels.idNumber')}: {teacher.id_number}</p>
            </div>
          </div>
        </div>

        {/* Professional Info Card */}
        <div className="bg-white rounded-xl shadow-lg overflow-hidden">
          <div className="p-6 border-b border-gray-100">
            <h2 className="text-2xl font-semibold text-gray-800">{t('sections.professionalInformation')}</h2>
          </div>
          <div className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <InfoItem icon={School} label={t('labels.tscNumber')} value={teacher.tsc_number} />
              <InfoItem icon={User} label={t('labels.gender')} value={teacher.gender} />
              <InfoItem 
                icon={Calendar} 
                label={t('labels.dateOfBirth')} 
                value={format(new Date(teacher.date_of_birth), 'dd MMM yyyy')} 
              />
              <InfoItem 
                icon={Calendar} 
                label={t('labels.dateOfJoining')} 
                value={format(new Date(teacher.date_of_joining), 'dd MMM yyyy')} 
              />
            </div>
          </div>
        </div>
      </div>

      {/* Contact Information Card */}
      <div className="bg-white rounded-xl shadow-lg overflow-hidden mb-8">
        <div className="p-6 border-b border-gray-100">
          <h2 className="text-2xl font-semibold text-gray-800">{t('sections.contactInformation')}</h2>
        </div>
        <div className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <InfoItem icon={Phone} label={t('labels.phone')} value={teacher.phone} />
            <InfoItem icon={Mail} label={t('labels.email')} value={teacher.email} />
            <InfoItem icon={MapPin} label={t('labels.address')} value={teacher.address} />
          </div>
        </div>
      </div>

      {/* Assigned Subjects Section */}
      <div className="bg-white rounded-xl shadow-lg overflow-hidden">
        <div className="p-6 border-b border-gray-100">
          <div className="flex items-center justify-between">
            <h2 className="text-2xl font-semibold text-gray-800 flex items-center gap-2">
              <BookOpen className="w-6 h-6" />
              {t('sections.assignedSubjects')}
            </h2>
            <button
              onClick={openAssignModal}
              className="px-4 py-2 bg-[#0E5252] text-white rounded-lg hover:bg-[#0E5252]/90 transition-colors flex items-center gap-2 text-sm"
            >
              <Plus className="w-4 h-4" />
              {t('buttons.assignNew')}
            </button>
          </div>
        </div>
        <div className="p-6">
          {loadingAssignments ? (
            <div className="flex items-center justify-center py-8">
              <Loader className="w-6 h-6 animate-spin text-[#0E5252]" />
            </div>
          ) : assignedSubjects.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <GraduationCap className="w-12 h-12 mx-auto mb-4 text-gray-300" />
              <p>{t('messages.noAssignments')}</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-200">
                    <th className="text-left py-3 px-4 font-semibold text-gray-700">{t('table.subject')}</th>
                    <th className="text-left py-3 px-4 font-semibold text-gray-700">{t('table.class')}</th>
                    <th className="text-left py-3 px-4 font-semibold text-gray-700">{t('table.stream')}</th>
                    <th className="text-left py-3 px-4 font-semibold text-gray-700">{t('table.academicYear')}</th>
                    <th className="text-left py-3 px-4 font-semibold text-gray-700">{t('table.actions')}</th>
                  </tr>
                </thead>
                <tbody>
                  {assignedSubjects.map((assignment) => (
                    <tr key={assignment.id} className="border-b border-gray-100 hover:bg-gray-50">
                      <td className="py-3 px-4">{getSubjectName(assignment.subject_id)}</td>
                      <td className="py-3 px-4">{getClassName(assignment.class_id)}</td>
                      <td className="py-3 px-4">{getStreamName(assignment.stream_id)}</td>
                      <td className="py-3 px-4">{getAcademicYearName(assignment.academic_year_id)}</td>
                      <td className="py-3 px-4">
                        <div className="flex gap-2">
                          <button
                            onClick={() => openEditModal(assignment)}
                            className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                            title={t('buttons.edit')}
                          >
                            <Edit className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => openDeleteModal(assignment)}
                            className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                            title={t('buttons.delete')}
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Assignment Modal */}
      {showAssignModal && (
        <Modal onClose={() => setShowAssignModal(false)} title={t('modals.assignSubject')}>
          <form onSubmit={handleAssignSubject} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('labels.subject')}
              </label>
              <select
                value={assignmentForm.subject_id}
                onChange={(e) => setAssignmentForm(prev => ({ ...prev, subject_id: e.target.value }))}
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0E5252] focus:border-transparent"
                required
              >
                <option value="">{t('placeholders.selectSubject')}</option>
                {subjects.map(subject => (
                  <option key={subject.id} value={subject.id}>
                    {subject.subject_name}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('labels.class')}
              </label>
              <select
                value={assignmentForm.class_id}
                onChange={(e) => {
                  setAssignmentForm(prev => ({ 
                    ...prev, 
                    class_id: e.target.value, 
                    stream_id: '' 
                  }));
                }}
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0E5252] focus:border-transparent"
                required
              >
                <option value="">{t('placeholders.selectClass')}</option>
                {classes.map(classItem => (
                  <option key={classItem.id} value={classItem.id}>
                    {classItem.name}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('labels.stream')}
              </label>
              <select
                value={assignmentForm.stream_id}
                onChange={(e) => setAssignmentForm(prev => ({ ...prev, stream_id: e.target.value }))}
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0E5252] focus:border-transparent"
                required
                disabled={!assignmentForm.class_id}
              >
                <option value="">{t('placeholders.selectStream')}</option>
                {getStreamsForClass(assignmentForm.class_id).map(stream => (
                  <option key={stream.id} value={stream.id}>
                    {stream.name}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('labels.academicYear')}
              </label>
              <select
                value={assignmentForm.academic_year_id}
                onChange={(e) => setAssignmentForm(prev => ({ ...prev, academic_year_id: e.target.value }))}
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0E5252] focus:border-transparent"
                required
              >
                <option value="">{t('placeholders.selectAcademicYear')}</option>
                {academicYears.map(year => (
                  <option key={year.id} value={year.id}>
                    {year.year_name} {year.is_current_year && `(${t('labels.current')})`}
                  </option>
                ))}
              </select>
            </div>

            <div className="flex gap-3 pt-4">
              <button
                type="submit"
                disabled={submitting || loadingData}
                className="flex-1 bg-[#0E5252] text-white py-3 px-4 rounded-lg hover:bg-[#0E5252]/90 transition-colors flex items-center justify-center gap-2 disabled:opacity-50"
              >
                {submitting ? (
                  <Loader className="w-4 h-4 animate-spin" />
                ) : (
                  <Save className="w-4 h-4" />
                )}
                {submitting ? t('buttons.assigning') : t('buttons.assign')}
              </button>
              <button
                type="button"
                onClick={() => setShowAssignModal(false)}
                disabled={submitting}
                className="flex-1 bg-gray-500 text-white py-3 px-4 rounded-lg hover:bg-gray-600 transition-colors disabled:opacity-50"
              >
                {t('buttons.cancel')}
              </button>
            </div>
          </form>
        </Modal>
      )}

      {/* Edit Modal */}
      {showEditModal && (
        <Modal onClose={() => setShowEditModal(false)} title={t('modals.editAssignment')}>
          <form onSubmit={handleUpdateAssignment} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('labels.subject')}
              </label>
              <select
                value={assignmentForm.subject_id}
                onChange={(e) => setAssignmentForm(prev => ({ ...prev, subject_id: e.target.value }))}
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0E5252] focus:border-transparent"
                required
              >
                <option value="">{t('placeholders.selectSubject')}</option>
                {subjects.map(subject => (
                  <option key={subject.id} value={subject.id}>
                    {subject.subject_name}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('labels.class')}
              </label>
              <select
                value={assignmentForm.class_id}
                onChange={(e) => {
                  setAssignmentForm(prev => ({ 
                    ...prev, 
                    class_id: e.target.value, 
                    stream_id: '' 
                  }));
                }}
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0E5252] focus:border-transparent"
                required
              >
                <option value="">{t('placeholders.selectClass')}</option>
                {classes.map(classItem => (
                  <option key={classItem.id} value={classItem.id}>
                    {classItem.name}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('labels.stream')}
              </label>
              <select
                value={assignmentForm.stream_id}
                onChange={(e) => setAssignmentForm(prev => ({ ...prev, stream_id: e.target.value }))}
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0E5252] focus:border-transparent"
                required
                disabled={!assignmentForm.class_id}
              >
                <option value="">{t('placeholders.selectStream')}</option>
                {getStreamsForClass(assignmentForm.class_id).map(stream => (
                  <option key={stream.id} value={stream.id}>
                    {stream.name}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('labels.academicYear')}
              </label>
              <select
                value={assignmentForm.academic_year_id}
                onChange={(e) => setAssignmentForm(prev => ({ ...prev, academic_year_id: e.target.value }))}
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0E5252] focus:border-transparent"
                required
              >
                <option value="">{t('placeholders.selectAcademicYear')}</option>
                {academicYears.map(year => (
                  <option key={year.id} value={year.id}>
                    {year.year_name} {year.is_current_year && `(${t('labels.current')})`}
                  </option>
                ))}
              </select>
            </div>

            <div className="flex gap-3 pt-4">
              <button
                type="submit"
                disabled={submitting || loadingData}
                className="flex-1 bg-[#0E5252] text-white py-3 px-4 rounded-lg hover:bg-[#0E5252]/90 transition-colors flex items-center justify-center gap-2 disabled:opacity-50"
              >
                {submitting ? (
                  <Loader className="w-4 h-4 animate-spin" />
                ) : (
                  <Save className="w-4 h-4" />
                )}
                {submitting ? t('buttons.updating') : t('buttons.update')}
              </button>
              <button
                type="button"
                onClick={() => setShowEditModal(false)}
                disabled={submitting}
                className="flex-1 bg-gray-500 text-white py-3 px-4 rounded-lg hover:bg-gray-600 transition-colors disabled:opacity-50"
              >
                {t('buttons.cancel')}
              </button>
            </div>
          </form>
        </Modal>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <Modal onClose={() => setShowDeleteModal(false)} title={t('modals.confirmDelete')}>
          <div className="text-center">
            <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100 mb-4">
              <Trash2 className="h-6 w-6 text-red-600" />
            </div>
            <p className="text-gray-700 mb-6">
              {t('messages.deleteConfirmation')}
            </p>
            <div className="flex gap-3">
              <button
                onClick={handleDeleteAssignment}
                disabled={submitting}
                className="flex-1 bg-red-600 text-white py-3 px-4 rounded-lg hover:bg-red-700 transition-colors flex items-center justify-center gap-2 disabled:opacity-50"
              >
                {submitting ? (
                  <Loader className="w-4 h-4 animate-spin" />
                ) : (
                  <Trash2 className="w-4 h-4" />
                )}
                {submitting ? t('buttons.deleting') : t('buttons.delete')}
              </button>
              <button
                type="button"
                onClick={() => setShowDeleteModal(false)}
                disabled={submitting}
                className="flex-1 bg-gray-500 text-white py-3 px-4 rounded-lg hover:bg-gray-600 transition-colors disabled:opacity-50"
              >
                {t('buttons.cancel')}
              </button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
};

// Helper component for info items
const InfoItem = ({ icon: Icon, label, value }) => (
  <div className="flex items-start gap-3">
    <Icon className="w-5 h-5 text-gray-500 mt-1" />
    <div>
      <p className="text-sm text-gray-500">{label}</p>
      <p className="text-base font-medium text-gray-800">{value || 'N/A'}</p>
    </div>
  </div>
);

// Modal Component
const Modal = ({ children, onClose, title }) => (
  <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
    <div className="bg-white rounded-xl shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto">
      <div className="flex items-center justify-between p-6 border-b border-gray-200">
        <h3 className="text-xl font-semibold text-gray-800">{title}</h3>
        <button
          onClick={onClose}
          className="text-gray-400 hover:text-gray-600 transition-colors"
        >
          <X className="w-6 h-6" />
        </button>
      </div>
      <div className="p-6">
        {children}
      </div>
    </div>
  </div>
);

export default TeacherProfile;